import { GenericShopRequestContext } from "@/abilities/shop/types/generic-shop";
import { getI18n } from "@/shared/i18n/i18n";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client";
import { useGenericShopCheckoutUrlLazyQuery } from "@/shared/services/graphql/generated/consumer-graph-types";
import { provideApolloClient } from "@vue/apollo-composable";

/**
 * Builds the request context for the checkout redirect. Unlike the auth
 * redirect (`load-generic-shop-session.ts`), there is no dedicated landing
 * page to return to — the redirect is same-tab (`use-generic-shop.ts`'s
 * `redirectToBasket`), so the default `returnPath` is simply the page the
 * user is on. Olaf mentioned a new tab might be intended instead (QIT-6923
 * Gap 10) — unconfirmed, tracked as a separate follow-up. The exact
 * callback/return contract is still open (QIT-6923 asks F, G, H).
 */
const buildRequestContext = (overrides?: Partial<GenericShopRequestContext>): GenericShopRequestContext => ({
    returnPath: window.location.pathname + window.location.search,
    locale: getI18n().global.locale.value,
    ...overrides,
});

/**
 * Resolves the URL to the shop's external checkout/basket view (QIT-7076).
 *
 * Returns `undefined` rather than throwing on failure — `redirectToBasket()`
 * resolves either way (see `use-generic-shop.ts`), and a console warning is
 * the only feedback a failed lookup can give.
 */
export const loadGenericShopCheckoutUrl = async (
    context?: Partial<GenericShopRequestContext>
): Promise<string | undefined> => {
    provideApolloClient(consumerClient);

    try {
        const { load } = useGenericShopCheckoutUrlLazyQuery({ context: buildRequestContext(context) });
        const data = await load();
        return data ? data.shopCheckoutUrl.url : undefined;
    } catch (error) {
        console.warn("Generic shop: checkout url could not be resolved.", error);
        return undefined;
    }
};
